import React, { useState } from 'react'
import { CButton } from '../../../../components/CButton'
import Modal from '@material-ui/core/Modal'
import { CreateDataset } from './NewDataset/CreateDataset'
import * as S from './ViewDataset.styles'
import { alertError, alertSuccess } from 'api'
import { cloneDatasetVersion } from 'tabs/data/api/datasets'
import { ConfirmCloneModal } from './CloneDataset/ConfirmCloneDataset'
import { Dataset, DatasetType } from '../../../../api/generated'
import { useDatasets } from '../../hooks/useDatasets'

type ViewDatasetDescriptionProps = {
  datasetId: string
  name: string
  description: string
  mandatoryDataPoints: string
  allDatasets: Dataset[]
  onOpenEdit: () => void
  refetch: () => void
}

export const ViewDataset: React.FC<ViewDatasetDescriptionProps> = ({
  datasetId,
  name,
  description,
  mandatoryDataPoints,
  allDatasets,
  onOpenEdit,
  refetch,
}) => {
  const { createDataset } = useDatasets()
  const [openDatasetModal, setOpenDatasetModal] = useState(false)
  const [openCloneModal, setOpenCloneModal] = useState(false)

  const resetDatasetForm = () => {
    setOpenDatasetModal(false)
  }

  const handleCreateDataset = async (
    name: string,
    description: string,
    mandatoryDataPoints: string,
    type: DatasetType,
    powerBiId: string,
    cloneFromId?: string,
    versionLabel?: string,
  ) => {
    const params = {
      datasetName: name,
      datasetDescription: description,
      datasetMandatoryDataPoints: mandatoryDataPoints,
      datasetPowerBiId: powerBiId,
      datasetType: type,
      cloneFromId: cloneFromId,
      datasetVersionLabel: versionLabel,
    }
    try {
      await createDataset(params)
      alertSuccess('Dataset has been created')
      refetch()
    } catch (error) {
      switch (error.response.status) {
        case 409:
          throw alertError('Dataset with this name already exists')
      }
      throw alertError('Failed to create dataset')
    }
  }

  const handleCloneDataset = async (versionLabel: string) => {
    try {
      await cloneDatasetVersion(datasetId, versionLabel)
      alertSuccess('Dataset has been cloned')
      refetch()
    } catch (error) {
      switch (error.response.status) {
        case 409:
          throw alertError(`Version label ${versionLabel} is already used for dataset ${name}`)
      }

      throw alertError('Failed to clone dataset')
    }
  }

  return (
    <S.Container>
      <S.SectionTitle>Description:</S.SectionTitle>
      <S.DescriptionText>{description}</S.DescriptionText>

      <S.SectionTitle>Question mandatory data points:</S.SectionTitle>
      <S.DescriptionText>{mandatoryDataPoints}</S.DescriptionText>

      <S.ActionsWrapper>
        <S.LeftButtons>
          <CButton variant="outlined" label="Edit configuration" onClick={onOpenEdit} />
          <CButton variant="outlined" label="Clone version" onClick={() => setOpenCloneModal(true)} />
        </S.LeftButtons>
        <CButton label="+ Add Dataset" color="primary" onClick={() => setOpenDatasetModal(true)} />
      </S.ActionsWrapper>

      <ConfirmCloneModal open={openCloneModal} onClose={() => setOpenCloneModal(false)} onClone={handleCloneDataset} />

      <Modal open={openDatasetModal}>
        <S.ModalContainer>
          <S.Title>New Dataset</S.Title>
          <CreateDataset
            datasetId={datasetId}
            allDatasets={allDatasets}
            onCancel={resetDatasetForm}
            onSubmit={handleCreateDataset}
          />
        </S.ModalContainer>
      </Modal>
    </S.Container>
  )
}
